<template>
  <div class="voice-room">
    <!-- Removed Close Button -->

    <VoiceRoomHeader
      :title="streamTitle"
      :room-id="roomInfo ? roomInfo.channelId : null"
      :host-avatar="require('@/assets/images/auth/female-avatar.png')"
      :gift-givers="[]"
      @settings="handleSettings"
      @close-stream="showEndStream"
    />

    <VoiceRoomSpeakers
      :main-host="mainHost"
      :speakers="speakers"
      :is-current-user-host="isHost"
      @request-speak="handleRequestSpeak"
    />

    <VoiceRoomChat
      :messages="messages"
      :is-host="isHost"
      :available-gifts="availableGifts"
      @send-message="handleSendMessage"
      @open-gift-panel="openGiftPanel"
      @send-quick-gift="handleSendQuickGift"
    />

    <VoiceRoomControls
      v-if="isHost"
      @toggle-mic="handleMicToggle"
    />

    <!-- Hediye satışı kaldırıldı — oda hediye modalı devre dışı
    <VoiceRoomGiftModal
      ref="giftModal"
      :host-name="mainHost ? mainHost.name : ''"
    />
    -->

    <!-- Host Settings Panel -->
    <HostSettingsPanel
      ref="hostSettingsPanel"
      :is-host-muted="isMuted"
      :channel-id="roomInfo ? roomInfo.channelId : null"
      @close-stream="endStream"
      @toggle-mic="handleMicToggle"
      @mute-participant="handleMuteParticipant"
      @unmute-participant="handleUnmuteParticipant"
      @kick-participant="handleKickParticipant"
      @panel-opened="loadGiftSenders"
    />

    <!-- Hediye satışı kaldırıldı — hediye animasyonu devre dışı
    <GiftAnimationOverlay
      ref="giftOverlay"
    />
    -->

    <!-- Speaker Request Modal -->
    <SpeakerRequestModal
      ref="speakerRequestModal"
      :visible="showSpeakerRequestModal"
      :request="firstPendingRequest"
      @accept="approveRequest"
      @reject="rejectRequest"
    />

    <!-- Kicked Info Modal -->
    <b-modal
      id="kicked-info-modal"
      v-model="showKickedModal"
      centered
      hide-header
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="text-center p-2">
        <h5 class="mb-1">{{ $t('voiceRoom.youWereKickedTitle') || 'Bilgi' }}</h5>
        <p class="mb-2">{{ $t('voiceRoom.youWereKicked') || 'Odadan atıldınız.' }}</p>
        <b-button variant="primary" @click="handleKickedModalClose">
          {{ $t('Okay') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Stream Ended Modal -->
    <b-modal
      v-model="showStreamHasEndedModal"
      centered
      hide-header
      hide-footer
    >
      <div class="text-center p-2">
        <h5 class="mb-1">{{ $t('voiceRoom.streamHasEnded') }}</h5>
        <b-button
          class="btn-primary2"
          @click="endStream"
        >
          {{ $t('Continue') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Stream End Modal -->
    <b-modal
      v-model="showEndStreamModal"
      centered
      hide-footer
    >
      <div class="text-center p-2">
        <h5
          v-if="mainHost"
          class="mb-1"
        >
          {{ mainHost.uuid === userId ? $t('streamEndText') : $t('You will exit the stream. Do you approve?') }}
        </h5>
        <div class="d-flex justify-content-between">
          <b-button
            variant="secondary"
            size="sm"
            @click="showEndStreamModal = false"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            v-if="mainHost"
            variant="danger"
            size="sm"
            @click="endStream"
          >
            {{ mainHost.uuid === userId ? $t('End stream') : $t('Exit from stream') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import VoiceRoomHeader from './components/VoiceRoomHeader.vue'
import VoiceRoomSpeakers from './components/VoiceRoomSpeakers.vue'
import VoiceRoomChat from './components/VoiceRoomChat.vue'
import VoiceRoomControls from './components/VoiceRoomControls.vue'
import VoiceRoomGiftModal from './components/VoiceRoomGiftModal.vue'
import HostSettingsPanel from './components/HostSettingsPanel.vue'
import GiftAnimationOverlay from './components/GiftAnimationOverlay.vue'
import SpeakerRequestModal from './components/SpeakerRequestModal.vue'
import { BModal, BButton } from 'bootstrap-vue'
import AgoraRTC from 'agora-rtc-sdk-ng'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import {
  getInitialSpeakersData,
  getVoiceRoomGoalsService,
  leaveAgoraChannelService,
  endStreamService,
  sendHeartbeatRequest,
  requestToBecomeSpeakerService,
  approveSpeakerRequestService,
  rejectSpeakerRequestService,
  initializeAgoraClient,
  joinAgoraChannel,
  setAgoraClientRole,
  publishLocalAudioTrack,
  leaveRoomAsAudienceService,
  getGiftSendersListService,
  sendVoiceRoomGift,
  toggleRemoteMuteService,
  subscribeVoiceRoom,
} from '@/services/VoiceRoomService'
import { showNotify, throwDefaultError } from '@/helpers/helpers'

const fallbackAvatar = require('@/assets/images/auth/female-avatar.png')

export default {
  name: 'VoiceRoom',
  components: {
    VoiceRoomHeader,
    VoiceRoomSpeakers,
    VoiceRoomChat,
    VoiceRoomControls,
    VoiceRoomGiftModal,
    HostSettingsPanel,
    GiftAnimationOverlay,
    SpeakerRequestModal,
    BModal,
    BButton,
  },
  data() {
    return {
      messages: [],
      agoraClient: null,
      localAudioTrack: null,
      agoraInitialized: false,
      heartbeatIntervalId: null,
      isEndingStream: false,
      showEndStreamModal: false,
      showKickedModal: false,
      showStreamHasEndedModal: false,
    }
  },
  computed: {
    roomInfo() {
      const storeInfo = this.$store.state.voiceRoom.roomInfo
      if (!storeInfo && !this.isEndingStream) {
        showNotify(this.$toast, this.$t('roomInfoNotFound'), 'info')
        this.endStream()
      }
      return storeInfo
    },
    ...mapGetters('voiceRoom', {
      isHost: 'isHost',
      isMuted: 'isMuted',
      pendingSpeakerRequests: 'pendingSpeakerRequests',
      hasPendingSpeakerRequests: 'hasPendingSpeakerRequests',
      mainHost: 'mainHost',
      streamTitle: 'streamTitle',
      speakers: 'speakers',
      giftSenders: 'giftSenders',
      availableGifts: 'availableGifts',
    }),
    ...mapGetters('socket', {
      socketConnection: 'connection',
    }),
    ...mapGetters('auth', {
      userId: 'userId',
    }),
    firstPendingRequest() {
      return this.pendingSpeakerRequests && this.pendingSpeakerRequests.length > 0
        ? this.pendingSpeakerRequests[0]
        : null
    },
    showSpeakerRequestModal: {
      get() {
        const shouldShow = !!this.firstPendingRequest && this.isHost
        return shouldShow
      },
      // We don't need a setter, visibility is controlled by firstPendingRequest
    },
  },

  async created() {
    // Log initial state
    console.log('VoiceRoom - created - Initial store roomInfo:', JSON.stringify(this.$store.state.voiceRoom.roomInfo))

    // Check route params and store state for refresh scenario
    const routeChannelId = this.$route.params.channelId
    const storeRoomInfo = this.$store.state.voiceRoom.roomInfo

    if (routeChannelId && !storeRoomInfo) {
      console.log(`VoiceRoom - created - Route param found (${routeChannelId}) and store is empty. Calling subscribeVoiceRoom directly...`)
      // Set loading state if you have one, e.g., this.loading = true;
      try {
        // Call the subscription service directly
        const roomInfo = await subscribeVoiceRoom(routeChannelId)

        // Commit the result directly to the store
        console.log('[VoiceRoom - created] Service successful, committing SET_ROOM_INFO:', roomInfo)
        this.$store.commit('voiceRoom/SET_ROOM_INFO', roomInfo)

        // Fetch gifts after successfully getting room info
        await this.fetchAvailableGifts()
        // Agora initialization will be triggered by the watch on roomInfo
      } catch (error) {
        console.error('VoiceRoom - created - Error calling subscribeVoiceRoom:', error)
        this.$toast({
          title: this.$t('Error'),
          text: error.message || this.$t('There was an unexpected error'),
          variant: 'danger',
        })
        // Redirect to home or another appropriate page on error
        this.$router.push({ name: 'popular-users' })
      } finally {
        // Reset loading state if you have one, e.g., this.loading = false;
      }
    } else if (storeRoomInfo) {
      console.log('VoiceRoom - created - Store already has roomInfo. Initializing directly.')
      // If store already has info (navigated normally), fetch gifts immediately
      await this.fetchAvailableGifts()
      // Watcher on roomInfo will handle initAgora
    } else {
      console.warn('VoiceRoom - created - No route param found or store info empty. Cannot initialize room properly.')
      // Decide how to handle this case, maybe redirect?
      // this.$router.push({ name: 'popular-users' })
      // Or maybe still try fetching gifts?
      await this.fetchAvailableGifts()
    }
  },

  watch: {
    roomInfo: {
      async handler(newVal) {
        if (newVal && !this.agoraInitialized) {
          await this.initAgora()
          await this.fetchAndSetRoomDetails()
        }
      },
      immediate: true,
    },
    showStreamHasEndedModal(value, oldValue) {
      if (value !== oldValue && !value) {
        this.endStream()
      }
    },
    socketConnection: {
      handler(newConnection, oldConnection) {
        if (newConnection && !oldConnection) {
          this.setupSocketListeners()
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId)
      this.heartbeatIntervalId = null
    }
    leaveAgoraChannelService(this.agoraClient, this.localAudioTrack)
  },
  methods: {
    ...mapActions('voiceRoom', [
      'setIsHost',
      'setIsMuted',
      'setSpeakers',
      'addSpeaker',
      'removeSpeaker',
      'processUserLeft',
      'handleSpeakerRequestApproved',
      'handleSpeakerRequestRejected',
      'setRoomDetails',
      'updateSpeakingStatus',
      'updateSpeaker',
      'fetchAvailableGifts',
    ]),
    ...mapMutations('voiceRoom', [
      'ADD_SPEAKER_REQUEST',
      'REMOVE_SPEAKER_REQUEST',
      'SET_MAIN_HOST',
      'SET_AUDIENCE_COUNT',
      'SET_GIFT_SENDERS',
    ]),

    handleSettings() {
      if (this.$refs.hostSettingsPanel) {
        this.$refs.hostSettingsPanel.openPanel()
      } else {
        console.error('HostSettingsPanel ref not found!')
      }
    },

    async handleRequestSpeak() {
      try {
        const success = await requestToBecomeSpeakerService()
        if (success) {
          showNotify(this.$toast, this.$t('requestToSpeakSent'), 'info')
        } else {
          console.warn('[SOCKET] Speaker request service call returned false (unexpected).')
          showNotify(this.$toast, this.$t('voiceRoom.requestErrorUnknown'), 'warning')
        }
      } catch (error) {
        console.error('[SOCKET] Error requesting speaker role via service:', error)
      }
    },

    setupSocketListeners() {
      if (!this.socketConnection) {
        console.warn('[SOCKET] Cannot setup listeners, connection is not available yet.')
        return
      }
      this.socketConnection.off('voiceRoomEvent').on('voiceRoomEvent', this.handleVoiceRoomEvent)
    },

    handleVoiceRoomEvent(data) {
      if (!data || !data.eventType) {
        console.warn('[SOCKET] Received voiceRoomEvent without eventType:', data)
        return
      }

      console.log('data.eventType', data.eventType)

      switch (data.eventType) {
        case 'be_publisher_request':
          this.handleBePublisherRequest(data)
          break
        case 'publisher_joined':
          this.handlePublisherJoined(data)
          break
        case 'subscriber_joined':
          this.handleSubscriberJoined(data)
          break
        case 'user_left':
          this.handleUserLeftEvent(data)
          break
        case 'channel_terminated':
        case 'channel_terminated_by_admin':
          this.handleChannelTerminated(data)
          break
        case 'chat_msg_sent':
          this.handleChatMsgSent(data)
          break
        case 'chat_history':
          this.handleChatHistory(data)
          break
        case 'user_kicked':
        case 'publisher_kicked':
          this.handleUserKicked(data)
          break
        case 'voice_state_toggled':
          this.handleVoiceStateToggled(data)
          break
        case 'gift_sent':
          this.handleIncomingGift(data)
          break
        default:
          console.log(`[SOCKET] Unhandled voiceRoomEvent type: ${data.eventType}`)
      }
    },

    handleBePublisherRequest(data) {
      if (this.isHost && data.userId && data.userName) {
        const request = {
          userId: data.userId,
          userName: data.userName,
          userAvatar: null, // TODO: Get avatar from data if available
          userUuid: data.userUuid,
        }
        this.ADD_SPEAKER_REQUEST(request)
        // Log store state after commit (original debug log location)
        this.$nextTick(() => {
          // console.log('[handleBePublisherRequest] Committed ADD_SPEAKER_REQUEST. Current pending requests in store:', JSON.stringify(this.$store.state.voiceRoom.pendingSpeakerRequests));
        })

        showNotify(this.$toast, this.$t('voiceRoom.newSpeakerRequest', { name: request.userName }), 'info')
      } else {
        // console.log('[SOCKET] Ignoring be_publisher_request (not host or missing user data). isHost:', this.isHost);
      }
    },

    handlePublisherJoined(data) {
      if (!data || !data.userId) {
        console.warn('[SOCKET] Invalid data received for publisher_joined event. Missing userId.')
        return
      }

      const joiningUserId = data.userId

      if (this.mainHost && this.mainHost.id === joiningUserId) {
        console.error(`[SOCKET] User ${joiningUserId} is already the main host. Updating info (if necessary).`)
        return
      }

      const isAlreadySpeaker = this.speakers.some(s => s.id === joiningUserId)
      if (isAlreadySpeaker) {
        console.error(`[SOCKET] User ${joiningUserId} is already in the speakers list. Updating info (if necessary).`)
        return
      }

      const newSpeaker = {
        id: joiningUserId,
        uuid: data.userUuid || null,
        name: data.userName || 'Unknown',
        avatar: data.userImage || fallbackAvatar,
        isActive: true,
        isMuted: false,
        level: data.userBadgeLevel === 'boho_member' ? 10 : null,
        agoraUid: data.agoraUid || null,
      }

      this.addSpeaker(newSpeaker)

      const myUserId = this.$store.state.auth.user?.identifier
      if (!this.isHost && myUserId && myUserId === joiningUserId) {
        // console.log(`[SOCKET] Promotion detected for self (User ID: ${myUserId}). Attempting to become publisher...`);
        this.becomePublisher()
      }
    },

    handleSubscriberJoined(data) {
      console.log('[SOCKET] Handling subscriber_joined:', data)
    },

    handleUserLeftEvent(data) {
      if (!data || !data.userId) {
        console.warn('[SOCKET] user_left event received without userId. Cannot process.')
        return
      }

      const leavingUserId = data.userId
      this.processUserLeft(leavingUserId)
    },

    async handleChannelTerminated(data) {
      console.warn('[SOCKET] Handling channel_terminated event:', data)

      // Prevent duplicate cleanup if already leaving
      if (this.isEndingStream) {
        console.error('[SOCKET] Channel terminated event received, but already ending stream. Skipping redundant cleanup.')
        return
      }
      this.isEndingStream = true // Mark as ending to prevent race conditions

      // Clear heartbeat immediately
      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId)
        this.heartbeatIntervalId = null
      }

      try {
        // Leave Agora channel cleanly (handles both host and audience)
        await leaveAgoraChannelService(this.agoraClient, this.localAudioTrack)

        // Clean up store
        this.$store.dispatch('voiceRoom/setRoomInfo', null)

        this.showStreamHasEndedModal = true
        // Redirect to home page (adjust route name if necessary)
        // this.$router.push({ name: 'popular-users' })
      } catch (error) {
        console.error('[SOCKET] Error during cleanup after channel termination:', error)
        this.showStreamHasEndedModal = true
        // Even if cleanup fails, try to redirect
        // this.$router.push({ name: 'popular-users' })
      } finally {
        // Reset the flag, although component might be destroyed soon
        this.isEndingStream = false
      }
    },

    handleChatMsgSent(data) {
      // 1. Prevent processing own message duplication (already added optimistically)
      const currentUserId = this.$store.state.auth.user?.id
      if (data.userId === currentUserId) {
        return
      }

      // 2. Check if necessary data exists in the new structure
      if (data.userName && data.content) {
        const newMessage = {
          type: 'text',
          sender: {
            name: data.userName, // Use userName from data
            avatar: data.profileImg || fallbackAvatar, // Use profileImg from data
          },
          content: data.content, // Use content from data
        }
        this.messages.push(newMessage)
      } else {
        // Update warning message to reflect expected fields
        console.warn('[SOCKET] chat_msg_sent event received with missing userName or content data.', data)
      }
    },

    handleChatHistory(data) {
      if (data.messages && Array.isArray(data.messages)) {
        this.messages = data.messages.map(msg => ({
          type: 'text',
          sender: {
            name: msg.userName || 'Unknown',
            avatar: msg.userImage || fallbackAvatar,
          },
          content: msg.message,
        }))
      } else {
        console.warn('[SOCKET] chat_history event received without a valid messages array.')
        this.messages = []
      }
    },

    handleUserKicked(data) {
      const kickedUserId = data.kickedUserId || data.userId

      if (!kickedUserId) {
        console.warn('[SOCKET] user_kicked event received without kickedUserId. Cannot process.')
        return
      }

      const currentUserId = this.$store.state.auth.user?.identifier

      if (kickedUserId === currentUserId) {
        console.warn('[SOCKET] Current user was kicked from the room!')
        this.showKickedModal = true
      } else {
        this.processUserLeft(kickedUserId)
      }
    },

    handlePublisherKicked(data) {
      console.log('[SOCKET] Handling publisher_kicked:', data)
    },

    handleIncomingGift(data) {
      // 1. Refresh the gift senders list from API
      this.loadGiftSenders()

      // 2. Trigger animation via ref
      if (data && data.giftRiv) {
        if (this.$refs.giftOverlay) {
          try {
            this.$refs.giftOverlay.play(data.giftRiv)
          } catch (e) {
            console.error('[SOCKET] handleIncomingGift: Error calling play() method:', e)
          }
        } else {
          console.warn('[SOCKET] handleIncomingGift: giftOverlay ref NOT found.')
        }
      } else {
        console.warn('[SOCKET] handleIncomingGift: giftRiv property missing or invalid in event data.', data)
      }

      // 3. Add a notification message to the chat
      if (data.sourceUserName && (data.giftLabel || data.giftProductCode) && data.giftCount) {
        const giftImageSrc = this.resolveGiftImagePathForChat(data.giftImage || data.giftRiv)
        const giftName = data.giftLabel || data.giftProductCode?.split('.').pop() || 'a gift' // Better fallback for name
        // eslint-disable-next-line global-require

        const giftMessage = {
          type: 'gift',
          sender: {
            name: data.sourceUserName,
            avatar: data.sourceUserImage || fallbackAvatar,
          },
          gift: {
            name: giftName,
            image: giftImageSrc,
          },
          count: data.giftCount || 1,
        }
        this.messages.push(giftMessage)
      } else {
        console.warn('[SOCKET] gift_sent event missing necessary data for chat message.', data)
      }

      // --- NEW: Dispatch action to update goal progress ---
      this.$store.dispatch('voiceRoom/processIncomingGift', data)
      // --- END NEW ---
    },

    // Helper method to resolve gift image path for chat (similar to HostSettingsPanel)
    resolveGiftImagePathForChat(imageIdentifier) {
      if (!imageIdentifier) return ''
      // Example: Assuming image identifier is just the filename like 'heart.svg' or 'heart.riv'
      // and images are in assets/images/gifts/
      const imageName = imageIdentifier.split('.')[0] // Extract name before extension
      try {
        // Prioritize svg or png if needed
        // eslint-disable-next-line global-require,import/no-dynamic-require
        return require(`@/assets/images/gifts/${imageName}.svg`)
      } catch (e) {
        try {
          // eslint-disable-next-line global-require,import/no-dynamic-require
          return require(`@/assets/images/gifts/${imageName}.png`)
        } catch (e2) {
          console.warn(`[Index.vue] Could not resolve gift image for chat: ${imageIdentifier}`)
          // eslint-disable-next-line global-require
          return require('@/assets/images/gifts/star.svg') // Example fallback
        }
      }
    },

    handleSendMessage(text) {
      // 1. Get User Info and Socket Connection
      const currentUser = this.$store.state.auth.user
      const userUuid = currentUser?.id
      // eslint-disable-next-line no-unused-vars
      const userName = currentUser?.userName || 'Me'
      // eslint-disable-next-line no-unused-vars,global-require
      const userAvatar = currentUser?.profile?.image || require('@/assets/images/auth/female-avatar.png')

      // 2. Check prerequisites (remains the same)
      if (!this.socketConnection) {
        console.warn('[SOCKET] Cannot send message, connection is not available.')
        return
      }
      if (!userUuid) {
        console.warn('[SOCKET] Cannot send message, user UUID (ID) is missing.')
        return
      }
      if (!text || !text.trim()) {
        console.warn('[SOCKET] Cannot send empty message.')
        return // Don't send empty messages
      }

      // 3. Construct the DTO (remains the same)
      const streamMsgDTO = {
        from: userUuid,
        content: text.trim(),
      }

      // 4. Emit the message via socket (remains the same)
      this.socketConnection.emit('voiceRoomMsg', streamMsgDTO)
    },

    openGiftPanel() {
      // Hediye satışı kaldırıldı — hediye paneli açılmıyor
      // this.$refs.giftModal.openChooseGiftModal()
    },

    async handleUserPublished(user, mediaType) {
      try {
        await this.agoraClient.subscribe(user, mediaType)

        if (mediaType === 'audio') {
          if (user.audioTrack) {
            try {
              await user.audioTrack.play()
            } catch (playError) {
              showNotify(this.$toast, this.$t('voiceRoom.audioAutoplayWarning'), 'warning')
            }
          } else {
            console.warn(`[DEBUG_AUDIO] Audio track not found for user ${user.uid} after subscribing.`)
          }
        }
      } catch (subscribeError) {
        console.error(`[DEBUG_AUDIO] Failed to subscribe to user ${user.uid}:`, subscribeError)
      }
    },

    handleUserUnpublished(user) {
      console.log(`[DEBUG_AUDIO] User ${user.uid} unpublished.`)
    },

    handleUserJoined(user) {
      console.log('User joined:', user.uid)
    },

    async handleUserLeft(user) {
      if (!user) {
        console.warn('[AGORA EVENT] handleUserLeft called with undefined user object. Skipping.')
        return
      }
      const agoraUid = user.uid
      if (!agoraUid) {
        console.warn('[AGORA EVENT] handleUserLeft called, but user.uid is undefined. Skipping.', user)
        return
      }
      console.log(`[AGORA EVENT] User left Agora channel. UID: ${agoraUid}. Dispatching processUserLeft action.`)
      this.processUserLeft(agoraUid)
    },

    handleVolumeIndicator(volumes) {
      // Log the raw data from Agora
      // console.log('Agora Volume Indicators:', volumes[0].level);

      // Dispatch an action to process these volumes and update the store
      this.updateSpeakingStatus(volumes)
    },

    async initAgora() {
      if (this.agoraInitialized || !this.roomInfo) {
        console.error(`[DEBUG] initAgora: Skipping initialization. Initialized: ${this.agoraInitialized}, RoomInfo: ${!!this.roomInfo}`)
        return
      }

      try {
        this.agoraClient = initializeAgoraClient(
          this.handleUserPublished,
          this.handleUserUnpublished,
          this.handleUserJoined,
          this.handleUserLeft,
        )

        // Enable Volume Indication AFTER client is created
        AgoraRTC.setParameter('AUDIO_VOLUME_INDICATION_INTERVAL', 200) // Check interval in ms
        this.agoraClient.enableAudioVolumeIndicator()

        // Listen for volume changes
        this.agoraClient.on('volume-indicator', this.handleVolumeIndicator)

        this.agoraInitialized = true

        const userObject = this.$store.state.auth.user
        const userIdentifierForAgora = userObject?.identifier
        if (!userIdentifierForAgora) {
          throw new Error('User identifier for Agora is missing.')
        }

        await joinAgoraChannel(this.agoraClient, this.roomInfo, userIdentifierForAgora)

        if (this.roomInfo.isHost) {
          // eslint-disable-next-line no-underscore-dangle
          await this._initializeAsHost()
        } else {
          // eslint-disable-next-line no-underscore-dangle
          await this._initializeAsAudience()
        }

        // eslint-disable-next-line no-underscore-dangle
        this._startHeartbeat()
        await this.fetchInitialSpeakers()
        await this.loadGiftSenders()
      } catch (error) {
        console.error('Agora initialization failed:', error)
        this.agoraInitialized = false
        this.SET_GIFT_SENDERS([])
      }
    },

    // eslint-disable-next-line no-underscore-dangle
    async _initializeAsHost() {
      this.setIsHost(true)
      try {
        await setAgoraClientRole(this.agoraClient, 'host')
        this.localAudioTrack = await publishLocalAudioTrack(this.agoraClient)

        if (this.localAudioTrack) {
          this.setIsMuted(!this.localAudioTrack.enabled)
        } else {
          console.warn('Host initialization completed, but localAudioTrack is missing.')
          this.setIsMuted(false)
        }
      } catch (error) {
        console.error('Failed to initialize as Host:', error)
        this.setIsHost(false)
        try {
          await setAgoraClientRole(this.agoraClient, 'audience')
        } catch (fallbackError) {
          console.error('Failed to fallback to Audience role:', fallbackError)
        }
      }
    },

    // eslint-disable-next-line no-underscore-dangle
    async _initializeAsAudience() {
      this.setIsHost(false)
      try {
        await setAgoraClientRole(this.agoraClient, 'audience')
        this.setIsMuted(false)
      } catch (error) {
        console.error('Failed to initialize as Audience:', error)
      }
    },

    async handleMicToggle() {
      const desiredEnabledState = this.isMuted // true if currently muted (want to unmute)
      const newMutedState = !desiredEnabledState // The state we are transitioning TO

      if (!this.isHost || !this.localAudioTrack || typeof this.localAudioTrack.setEnabled !== 'function') {
        console.warn('Mic toggle attempted by non-host or track not ready/invalid.')
        return
      }

      try {
        // 1. Toggle local track first
        await this.localAudioTrack.setEnabled(desiredEnabledState)

        // 2. Call the backend service to sync the self-mute status using the remote mute service
        const currentUserIdentifier = this.$store.state.auth.user?.id
        const currentChannelId = this.roomInfo?.channelId

        if (currentUserIdentifier && currentChannelId) {
          try {
            console.log(`Calling toggleRemoteMuteService for self (user ${currentUserIdentifier} in channel ${currentChannelId}) with muted status: ${newMutedState}`)
            // Pass channel ID, current user ID, and the NEW mute state to the service
            await toggleRemoteMuteService(currentChannelId, currentUserIdentifier)
            console.log('Backend self-mute status update successful via remote service.')
          } catch (serviceError) {
            console.error('Error calling toggleRemoteMuteService for self:', serviceError)
            // Optional: Notify user about sync failure
            // showNotify(this.$toast, 'Mic status could not be synced with the server.', 'warning');
          }
        } else {
          console.warn('Cannot sync mic status with backend: User identifier or Channel ID not found.')
        }

        // 3. Update local Vuex state AFTER local toggle and backend call attempt
        this.setIsMuted(newMutedState)
      } catch (error) {
        console.error('Error toggling mic locally:', error)
        showNotify(this.$toast, this.$t('voiceRoom.micToggleError'), 'danger', { icon: 'AlertCircleIcon' })
      }
    },

    // eslint-disable-next-line no-underscore-dangle
    _startHeartbeat() {
      if (!this.roomInfo || !this.roomInfo.heartbeatIntervalInSecs) {
        console.error('Heartbeat interval information is missing from roomInfo. Cannot start heartbeat.')
        return
      }

      const intervalSeconds = this.roomInfo.heartbeatIntervalInSecs
      const intervalMilliseconds = intervalSeconds * 1000

      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId)
      }

      // eslint-disable-next-line no-underscore-dangle
      this._sendHeartbeat()
      // eslint-disable-next-line no-underscore-dangle
      this.heartbeatIntervalId = setInterval(this._sendHeartbeat, intervalMilliseconds)
    },

    // eslint-disable-next-line no-underscore-dangle
    async _sendHeartbeat() {
      if (!this.roomInfo || !this.roomInfo.channelName) {
        console.warn('[Index.vue] Skipping heartbeat: roomInfo or channelName not available.')
        return
      }
      try {
        const heartbeatResponse = await sendHeartbeatRequest(this.roomInfo.channelName)
        // console.log('[Index.vue] Heartbeat response received:', heartbeatResponse);
        if (heartbeatResponse && typeof heartbeatResponse.nbOfAudience === 'number') {
          // console.log(`[Index.vue] Updating audience count to: ${heartbeatResponse.nbOfAudience}`);
          this.SET_AUDIENCE_COUNT(heartbeatResponse.nbOfAudience)
        }
      } catch (error) {
        // Error is already logged in the service, maybe stop interval here?
        console.error('[Index.vue] Error during _sendHeartbeat call:', error)
        // Consider stopping heartbeat if it fails consistently
        // clearInterval(this.heartbeatIntervalId);
        // this.heartbeatIntervalId = null;
      }
    },

    async endStream() {
      if (this.isEndingStream) return
      this.isEndingStream = true

      // Clear heartbeat immediately
      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId)
        this.heartbeatIntervalId = null
      }

      let leaveSuccess = false
      try {
        if (this.isHost) {
          // Host ends the stream for everyone
          leaveSuccess = await endStreamService(this.agoraClient, this.localAudioTrack)
          if (!leaveSuccess) {
            console.error('endStreamService (Host) reported failure.')
          }
        } else {
          // Audience just leaves the Agora channel
          leaveSuccess = await leaveRoomAsAudienceService(this.agoraClient)
          if (!leaveSuccess) {
            console.error(' leaveRoomAsAudienceService reported failure.')
          }
        }

        // Common cleanup regardless of role, only if leaving Agora succeeded
        if (leaveSuccess) {
          this.$store.dispatch('voiceRoom/setRoomInfo', null)
          // No need to set isHost/isMuted here, setRoomInfo action handles it
          if (this.isHost) {
            this.$router.push({ name: 'voice-room-setup' })
          } else {
            this.$router.push({ name: 'popular-users' }) // Redirect audience to popular users
          }
        } else {
          console.log('Leave failed, not cleaning up store or routing.')
          // Maybe re-enable heartbeat if leaving failed?
        }
      } catch (error) {
        // Catch errors from the service calls themselves (e.g., network issues)
        console.error('Unexpected error during endStream/leaveRoom:', error)
        leaveSuccess = false // Ensure cleanup doesn't run on unexpected error
      } finally {
        this.isEndingStream = false
      }
    },

    showEndStream() {
      console.log('showEndStream')
      this.showEndStreamModal = true
    },

    async approveRequest(userId) {
      if (!userId) {
        console.error('approveRequest called with invalid userId.')
        return
      }
      const request = this.pendingSpeakerRequests.find(r => r.userId === userId)
      if (!request || !request.userUuid) {
        console.error(`Cannot find request or userUuid for user ID: ${userId}`)
        if (this.$refs.speakerRequestModal) this.$refs.speakerRequestModal.hideModal()
        return
      }

      try {
        const success = await approveSpeakerRequestService(request.userUuid)
        if (success) {
          const speakerData = {
            id: request.userId,
            name: request.userName,
            avatar: request.userAvatar || fallbackAvatar,
            uuid: request.userUuid,
            isActive: true,
            isMuted: false,
            agoraUid: null, // Agora UID might come later via socket event
          }
          this.handleSpeakerRequestApproved({ userId: request.userId, speakerData })
          // showNotify(this.$toast, `İstek onaylandı (ID: ${userId})`, 'success');
        } else {
          console.warn(`Approve request service call failed for user: ${userId}`)
          // Still remove the request from the list on server failure?
          this.handleSpeakerRequestRejected(userId) // Remove locally if server fails?
        }
      } catch (error) {
        console.error(`Error approving request for user ${userId}:`, error)
        this.handleSpeakerRequestRejected(userId)
      } finally {
        // --- Close Modal ---
        if (this.$refs.speakerRequestModal) {
          this.$refs.speakerRequestModal.hideModal()
        }
        // --- End Close Modal ---
      }
    },

    async rejectRequest(userId) {
      if (!userId) {
        console.error('rejectRequest called with invalid userId.')
        return
      }
      const request = this.pendingSpeakerRequests.find(r => r.userId === userId)
      if (!request || !request.userUuid) {
        console.error(`Cannot find request or userUuid for user ID: ${userId}`)
        if (this.$refs.speakerRequestModal) this.$refs.speakerRequestModal.hideModal()
        return
      }

      try {
        const success = await rejectSpeakerRequestService(request.userUuid)
        if (success) {
          this.handleSpeakerRequestRejected(userId)
        } else {
          console.warn(`Reject request service call failed for user: ${userId}`)
          this.handleSpeakerRequestRejected(userId) // Remove locally if server fails?
        }
      } catch (error) {
        console.error(`Error rejecting request for user ${userId}:`, error)
        this.handleSpeakerRequestRejected(userId)
      } finally {
        // --- Close Modal ---
        if (this.$refs.speakerRequestModal) {
          this.$refs.speakerRequestModal.hideModal()
        }
        // --- End Close Modal ---
      }
    },

    async becomePublisher() {
      if (this.isHost || !this.agoraClient) {
        console.warn('Already host or Agora client not ready. Aborting role change.')
        return
      }
      try {
        await setAgoraClientRole(this.agoraClient, 'host')
        const newTrack = await publishLocalAudioTrack(this.agoraClient)
        this.localAudioTrack = newTrack
        this.setIsHost(true)
        if (this.localAudioTrack) {
          this.setIsMuted(!this.localAudioTrack.enabled)
        } else {
          console.warn('Became publisher via service, but new track is missing.')
          this.setIsMuted(false)
        }
        showNotify(this.$toast, this.$t('Promoted to speaker'), 'success')
      } catch (error) {
        console.error('Error becoming publisher via service:', error)
        this.setIsHost(false)
      }
    },

    async fetchInitialSpeakers() {
      if (!this.roomInfo || !this.roomInfo.channelId || !this.roomInfo.mainPublisherUuid) {
        console.error('Cannot fetch speakers: Required roomInfo fields missing.')
        return
      }

      const { channelId, mainPublisherUuid } = this.roomInfo

      try {
        const speakersData = await getInitialSpeakersData(channelId, mainPublisherUuid)

        console.log('speakersData', speakersData)

        this.SET_MAIN_HOST(speakersData.mainHost)
        this.setSpeakers(speakersData.speakers)
      } catch (error) {
        console.error('Error calling getInitialSpeakersData service:', error)
        this.SET_MAIN_HOST(null)
        this.setSpeakers([])
      }
    },

    handleMuteParticipant(participantId) {
      console.log(`Request to MUTE participant ID: ${participantId}`)
    },

    handleUnmuteParticipant(participantId) {
      console.log(`Request to UNMUTE participant ID: ${participantId}`)
    },

    handleKickParticipant(participantId) {
      console.log(`Received kick request for participant ID: ${participantId}. This handler might be deprecated.`)
    },

    async fetchAndSetRoomDetails() {
      if (!this.roomInfo || !this.roomInfo.channelId) {
        console.error('Cannot fetch room details: roomInfo or channelName missing.')
        this.setRoomDetails({ streamTitle: this.$t('No Title'), goals: [] })
        return
      }
      try {
        const response = await getVoiceRoomGoalsService(this.roomInfo.channelId)
        if (response && response.streamTitle && response.goals) {
          this.setRoomDetails({
            streamTitle: response.streamTitle,
            goals: response.goals,
          })
        } else {
          console.warn('Invalid room details response received:', response)
          this.setRoomDetails({ streamTitle: this.$t('No Title'), goals: [] })
        }
      } catch (error) {
        console.error('Error fetching room details:', error)
        this.setRoomDetails({ streamTitle: this.$t('No Title'), goals: [] })
      }
    },

    async handleVoiceStateToggled(data) {
      // console.log('[SOCKET] Handling voice_state_toggled event:', data);

      const { userId } = data
      const newMuteState = data.voiceMuted

      if (userId === undefined || newMuteState === undefined) {
        // console.warn('[SOCKET] voice_state_toggled event missing userId or voiceMuted state.', data);
        return
      }

      // --- Apply local mute/unmute via Agora SDK if this client is the target ---
      const currentUserId = this.$store.state.auth.user?.identifier
      if (userId === currentUserId) {
        if (this.localAudioTrack && typeof this.localAudioTrack.setEnabled === 'function') {
          try {
            const desiredEnabledState = !newMuteState
            await this.localAudioTrack.setEnabled(desiredEnabledState)

            this.setIsMuted(newMuteState)
          } catch (error) {
            console.error(`Error calling localAudioTrack.setEnabled(${!newMuteState}):`, error)
          }
        } else {
          console.warn('Cannot apply local mute/unmute: localAudioTrack is not available or invalid.')
        }
      }
      // --- End of local Agora action ---

      // Dispatch action to update the store for UI consistency for everyone
      this.updateSpeaker({ id: userId, isMuted: newMuteState })
    },

    async loadGiftSenders() {
      if (!this.roomInfo || !this.roomInfo.channelId) {
        console.error('Cannot load gift senders: roomInfo or channelId missing.')
        return
      }
      try {
        const senders = await getGiftSendersListService(this.roomInfo.channelId)
        this.SET_GIFT_SENDERS(senders)
      } catch (error) {
        console.error('Error loading gift senders:', error)
        this.SET_GIFT_SENDERS([])
      }
    },

    // New method to handle quick gift sending
    async handleSendQuickGift(gift) {
      if (!gift || !gift.productCode) {
        console.error('Invalid gift data received for quick send.')
        return
      }

      const giftData = { productCode: gift.productCode }

      try {
        // Call the service function
        const sendGiftResult = await sendVoiceRoomGift(giftData)

        console.log('sendGiftResult', sendGiftResult, gift, typeof sendGiftResult.result !== 'undefined' && sendGiftResult.result)

        if (typeof sendGiftResult.result !== 'undefined' && sendGiftResult.result) {
          console.log('444444444')
          // --- Locally trigger animation for sender ---
          if (gift.riv && this.$refs.giftOverlay) {
            this.$refs.giftOverlay.play(gift.riv)
          }
          console.log('22222')
          // --- End local animation trigger ---
        } else if (typeof sendGiftResult.error !== 'undefined') {
          showNotify(this.$toast, sendGiftResult.error, 'danger')
        }

        console.log('11111', sendGiftResult)
      } catch (error) {
        console.log('333333')
        console.error('Error sending quick gift:', error)
        throwDefaultError(this.$toast, error) // Use the helper for consistent error display
      }
    },

    // New method to handle modal close and redirect
    handleKickedModalClose() {
      this.showKickedModal = false
      this.$router.push({ name: 'popular-users' })
    },
  },
}
</script>

<style lang="scss" scoped>
.voice-room {
  min-height: 90vh;
  background: linear-gradient(135deg, #2A0F38 0%, #13071C 100%);
  color: white;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;

  .voice-room-header,
  .voice-room-speakers {
    flex-shrink: 0;
  }

  .voice-room-chat {
    margin-top: auto;
    flex-shrink: 0;
  }
}

.speaker-requests-temp {
  background-color: rgba(0, 0, 0, 0.3);
  padding: 10px;
  margin: 10px 20px;
  border-radius: 5px;
  color: white;

  h5 {
    margin-bottom: 5px;
    font-size: 1rem;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  li {
    padding: 5px 0;
    display: flex;
    align-items: center;
  }
}
</style>
